import { useEffect, useMemo, useState } from 'react'
import { trackCalendarViewed } from '@/lib/analytics-events'
import { Link } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import { EmptyState } from '@/components/shared/EmptyState'
import { useFilterDefaultsFromPrefs } from '@/hooks/useFilterDefaultsFromPrefs'
import { formatDate } from '@/lib/utils'
import {
  useRegulatoryDeadlines,
  useRegulatoryFrameworks,
  type RegulatoryCalendarFilters,
  type RegulatoryDeadlineGroup,
  type RegulatoryDeadlineKind,
  type RegulatoryDeadlineRow,
  type RegulatoryDeadlineWindow,
} from '@/hooks/useRegulatoryQueries'

const DEFAULT_FILTERS: RegulatoryCalendarFilters = {
  framework: 'all',
  kind: 'all',
  // KAN-256 — `all` by default. A calendar whose real milestones are years
  // out must not open on a view that hides them.
  window: 'all',
}

const DEADLINE_DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
})

const WINDOW_OPTIONS: Array<{ value: RegulatoryDeadlineWindow; label: string }> = [
  { value: 'all', label: 'All upcoming' },
  { value: '30', label: 'Next 30 days' },
  { value: '90', label: 'Next 90 days' },
  { value: '365', label: 'Next 365 days' },
]

const KIND_OPTIONS: Array<{ value: RegulatoryDeadlineKind | 'all'; label: string }> = [
  { value: 'all', label: 'All kinds' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'amendment', label: 'Amendment' },
  { value: 'transition', label: 'Transition' },
  { value: 'reporting', label: 'Reporting' },
  { value: 'consultation_close', label: 'Consultation close' },
]

const MONTH_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

function monthKey(dueDate: string): string {
  const [year, month] = dueDate.split('-')
  return `${year}-${month}`
}

function monthLabel(dueDate: string): string {
  const [yearPart, monthPart, dayPart] = dueDate.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart ?? '1')
  if (!Number.isFinite(year) || !Number.isFinite(month)) return dueDate
  return MONTH_FORMATTER.format(new Date(Date.UTC(year, month - 1, day || 1)))
}

function quarterLabel(quarter: string): string {
  const match = quarter.match(/^(\d{4})Q([1-4])$/)
  if (!match) return quarter
  return `${match[1]} Q${match[2]}`
}

export function formatDeadlineDate(dueDate: string): string {
  const [yearPart, monthPart, dayPart] = dueDate.split('-')
  const year = Number(yearPart)
  const month = Number(monthPart)
  const day = Number(dayPart)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return formatDate(dueDate)
  }
  return DEADLINE_DATE_FORMATTER.format(new Date(Date.UTC(year, month - 1, day)))
}

type MonthBucket = {
  monthKey: string
  monthLabel: string
  deadlines: RegulatoryDeadlineRow[]
}

function bucketByMonth(group: RegulatoryDeadlineGroup): MonthBucket[] {
  const sorted = [...group.deadlines].sort((a, b) =>
    a.dueDate.localeCompare(b.dueDate),
  )
  const buckets = new Map<string, MonthBucket>()
  for (const row of sorted) {
    const key = monthKey(row.dueDate)
    const existing = buckets.get(key)
    if (existing) {
      existing.deadlines.push(row)
    } else {
      buckets.set(key, {
        monthKey: key,
        monthLabel: monthLabel(row.dueDate),
        deadlines: [row],
      })
    }
  }
  return Array.from(buckets.values()).sort((a, b) =>
    a.monthKey.localeCompare(b.monthKey),
  )
}

function formatKind(kind: RegulatoryDeadlineKind): string {
  return kind.replace(/_/g, ' ')
}

export function RegulatoryCalendarPage() {
  const { defaults: prefDefaults, hasSavedDefault } =
    useFilterDefaultsFromPrefs<RegulatoryCalendarFilters>('regulatory-calendar')
  const [filters, setFilters] = useState<RegulatoryCalendarFilters>(DEFAULT_FILTERS)

  // KAN-32 regulatory:calendar_viewed
  useEffect(() => {
    trackCalendarViewed(filters.window)
  }, [filters.window])
  const [userChangedFilters, setUserChangedFilters] = useState(false)

  useEffect(() => {
    if (hasSavedDefault && prefDefaults && !userChangedFilters) {
      setFilters({ ...DEFAULT_FILTERS, ...prefDefaults })
    }
  }, [hasSavedDefault, prefDefaults, userChangedFilters])

  const updateFilters = (updater: (prev: RegulatoryCalendarFilters) => RegulatoryCalendarFilters) => {
    setUserChangedFilters(true)
    setFilters(updater)
  }

  const deadlinesQuery = useRegulatoryDeadlines(filters)
  const frameworksQuery = useRegulatoryFrameworks()

  const frameworkOptions = useMemo(
    () =>
      (frameworksQuery.data?.frameworks ?? []).map((f) => ({
        slug: f.slug,
        name: f.name,
      })),
    [frameworksQuery.data],
  )

  const groups = deadlinesQuery.data?.groups ?? []
  const totalDeadlines = groups.reduce(
    (sum, group) => sum + group.deadlines.length,
    0,
  )

  return (
    <main
      className="mx-auto flex max-w-6xl flex-col gap-6 p-6"
      aria-label="Regulatory calendar"
      data-testid="regulatory-calendar-page"
    >
      <nav aria-label="breadcrumb" className="text-xs text-muted-foreground">
        <Link
          to="/regulatory"
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          Regulatory radar
        </Link>
      </nav>

      <header>
        <h1 className="text-2xl font-semibold">Regulatory calendar</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Upcoming regulatory deadlines, transitions, and consultation closes
          across published frameworks.
        </p>
      </header>

      <section
        aria-label="Calendar filters"
        className="flex flex-wrap items-end gap-4 rounded-lg border bg-card p-4"
      >
        <FilterField
          id="calendar-window"
          label="Window"
          value={filters.window}
          options={WINDOW_OPTIONS}
          onChange={(value) =>
            updateFilters((prev) => ({
              ...prev,
              window: value as RegulatoryDeadlineWindow,
            }))
          }
        />
        <FilterField
          id="calendar-kind"
          label="Kind"
          value={filters.kind}
          options={KIND_OPTIONS}
          onChange={(value) =>
            updateFilters((prev) => ({
              ...prev,
              kind: value as RegulatoryDeadlineKind | 'all',
            }))
          }
        />
        <FilterField
          id="calendar-framework"
          label="Framework"
          value={filters.framework}
          options={[
            { value: 'all', label: 'All frameworks' },
            ...frameworkOptions.map((option) => ({
              value: option.slug,
              label: option.name,
            })),
          ]}
          onChange={(value) =>
            updateFilters((prev) => ({ ...prev, framework: value }))
          }
        />
      </section>

      {deadlinesQuery.isLoading ? (
        <CalendarSkeleton />
      ) : deadlinesQuery.isError ? (
        <ErrorState
          title="Could not load regulatory deadlines"
          message="The deadline agenda may be temporarily unavailable."
          onRetry={() => deadlinesQuery.refetch()}
        />
      ) : totalDeadlines === 0 ? (
        <EmptyState
          title="No upcoming regulatory deadlines"
          description={
            filters.window === 'all'
              ? 'No deadlines match the current filters.'
              : 'No deadlines match the current filters in the selected window. Try "All upcoming".'
          }
        />
      ) : (
        <div className="flex flex-col gap-8">
          {groups.map((group) => (
            <QuarterSection key={group.quarter} group={group} />
          ))}
        </div>
      )}
    </main>
  )
}

function FilterField<TValue extends string>({
  id,
  label,
  value,
  options,
  onChange,
}: {
  id: string
  label: string
  value: TValue
  options: ReadonlyArray<{ value: string; label: string }>
  onChange: (next: string) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 min-w-[10rem] rounded-md border bg-background px-3 text-sm"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function QuarterSection({ group }: { group: RegulatoryDeadlineGroup }) {
  const buckets = useMemo(() => bucketByMonth(group), [group])
  const label = quarterLabel(group.quarter)
  return (
    <section
      aria-label={label}
      className="rounded-lg border bg-card p-4"
    >
      <header className="mb-3 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold">{label}</h2>
        <span className="text-xs text-muted-foreground">
          {group.deadlines.length} deadline
          {group.deadlines.length === 1 ? '' : 's'}
        </span>
      </header>
      <div className="flex flex-col gap-6">
        {buckets.map((bucket) => (
          <div key={bucket.monthKey} className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {bucket.monthLabel}
            </h3>
            <ul className="flex flex-col gap-3">
              {bucket.deadlines.map((deadline) => (
                <li key={deadline.id}>
                  <DeadlineRow deadline={deadline} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  )
}

function DeadlineRow({ deadline }: { deadline: RegulatoryDeadlineRow }) {
  return (
    <article className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium tabular-nums text-foreground">
          {formatDeadlineDate(deadline.dueDate)}
        </span>
        <Badge variant="secondary" className="font-normal">
          {formatKind(deadline.kind)}
        </Badge>
        <Link
          to={`/regulatory/frameworks/${deadline.frameworkSlug}`}
          className="text-sm font-medium text-primary hover:underline"
        >
          {deadline.frameworkName}
        </Link>
      </div>
      <p className="text-sm font-medium text-foreground">{deadline.label}</p>
      {deadline.description ? (
        <p className="text-sm text-muted-foreground">{deadline.description}</p>
      ) : null}
      {deadline.sourceUrl ? (
        <a
          href={deadline.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Source
          <ExternalLink className="h-3 w-3" aria-hidden />
        </a>
      ) : null}
    </article>
  )
}

function CalendarSkeleton() {
  return (
    <div className="flex flex-col gap-6" data-testid="calendar-skeleton">
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  )
}
